import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import api, { refreshAccessToken } from '../services/api';
import { getApiUrl } from '../config/runtimeConfig';

import { getAccessToken, clearAuthStorage } from '../services/api';

const SOCKET_URL = getApiUrl().replace(/\/api$/, '');

export default function MatchCountdownPage() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const locationState = useLocation().state || {};
  const token = getAccessToken();
  const socketRef = useRef(null);

  const [count, setCount] = useState(5);
  const [countText, setCountText] = useState('');
  const [phase, setPhase] = useState('countdown'); // countdown | go | started | cancelled
  const [gameType, setGameType] = useState(locationState.gameType || 'whot');
  const [errorMsg, setErrorMsg] = useState('');

  function navigateToWhot(gId) {
    if (!gId) {
      setErrorMsg('Invalid Stake game. Returning to Stake lobby...');
      navigate('/', { replace: true, state: { message: 'Invalid stake game. Returning to the game lobby.' } });
      return;
    }
    if (String(gameType || 'whot').toLowerCase() !== 'whot') {
      setErrorMsg('This Stake match is not a supported Whot game. Returning to Stake lobby...');
      navigate('/', { replace: true, state: { message: 'This stake match is not a supported Whot game.' } });
      return;
    }
    navigate(`/play/whot/${gId}`, {
      state: {
        matchSource: 'stake',
        matchId: gId,
        tierId: locationState.tierId,
        gameType: 'whot',
        entryFee: locationState.entryFee,
      },
      replace: true,
    });
  }

  // ============================================================
  // SOCKET CONNECTION
  // ============================================================

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    if (!matchId) {
      navigate('/', { replace: true });
      return;
    }

    const socket = io(SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });

    socketRef.current = socket;

    // ── Connection safety timeout ──
    let countdownTimeout = setTimeout(() => {
      if (phase === 'countdown' && countText === '') {
        console.warn('[Countdown] No countdown events received after 12s — connection issue');
        setErrorMsg('Connection issue. Returning to stake page...');
        setTimeout(() => navigate('/', { replace: true, state: { message: 'Connection issue. Returning to the game lobby.' } }), 2500);
      }
    }, 12000);

    socket.on('connect', async () => {
      let active = null;
      try {
        const response = await api.stakes.getActive();
        active = response.data?.data || null;
      } catch (err) {
        console.warn('[Countdown] authoritative match recovery failed', err);
      }

      const tierIdToUse = active?.tierId || locationState.tierId || null;
      const authoritativeGameType = active?.gameType || locationState.gameType || 'whot';
      if (active?.matchId && String(active.matchId) !== String(matchId)) {
        console.warn('[Countdown] active match differs from URL; recovering authoritative match', active.matchId);
        navigate(`/match/${active.matchId}/countdown`, { replace: true, state: active });
        return;
      }
      if (authoritativeGameType !== gameType) setGameType(authoritativeGameType);
      if (tierIdToUse) socket.emit('stake:joined', { tierId: tierIdToUse });
      socket.emit('stake:countdownReady', { gameId: matchId });
    });

    socket.on('connect_error', async (err) => {
      const message = String(err?.message || '').toLowerCase();
      if (message.includes('token') || message.includes('expired') || message.includes('authentication')) {
        try {
          const fresh = await refreshAccessToken();
          socket.auth = { token: fresh };
          socket.connect();
        } catch (_) {}
      }
    });

    // ── Countdown ticks from server (room broadcast: match-{gameId}) ──
    socket.on('match:countdown', (data) => {
      console.log('[Countdown] match:countdown received', data);
      clearTimeout(countdownTimeout);
      if (data.text === 'GO') {
        setPhase('go');
        setCountText('GO!');
        return;
      }
      if (data.count !== undefined) {
        if (data.count <= 0) {
          setPhase('go');
          setCountText('GO!');
        } else {
          setCount(data.count);
          setCountText(String(data.count));
        }
      }
    });

    // ── Countdown-ready response: server replies with current state ──
    //     This catches us up if we connected after countdown already started.
    socket.on('stake:countdownReady', (data) => {
      console.log('[Countdown] stake:countdownReady response', data);
      if (data.state === 'in_progress') {
        // Game already started — navigate immediately
        setPhase('started');
        navigateToWhot(data.gameId || matchId);
        return;
      }
      if (data.countdownValue !== undefined && data.countdownValue > 0) {
        clearTimeout(countdownTimeout);
        setPhase('countdown');
        setCount(data.countdownValue);
        setCountText(String(data.countdownValue));
      }
    });

    // ── Game started → navigate to game ──
    socket.on('match:started', (data) => {
      setPhase('started');
      const gId = data.matchId || matchId;

      console.log('[Countdown] match:started — navigating to Whot', { matchId: gId });

      // Short delay so user sees GO! before transition
      setTimeout(() => navigateToWhot(gId), 600);
    });

    // ── Match cancelled (server-driven) ──
    socket.on('match:cancelled', (data) => {
      console.log('[Countdown] match:cancelled', data);
      setPhase('cancelled');
      setErrorMsg(data?.message || 'Match cancelled. You have been refunded.');

      setTimeout(() => {
        navigate('/', { replace: true, state: { message: data?.message || 'Match cancelled, stake refunded.' } });
      }, 3500);
    });

    // Also listen for legacy events
    socket.on('stake:cancelled', (data) => {
      setPhase('cancelled');
      setErrorMsg(data?.message || 'Match cancelled. You have been refunded.');
      setTimeout(() => {
        navigate('/', { replace: true, state: { message: data?.message || 'Match cancelled, stake refunded.' } });
      }, 3500);
    });

    socket.on('stake:countdown', (data) => {
      console.log('[Countdown] stake:countdown received', data);
      clearTimeout(countdownTimeout);
      if (data.value === 'GO') {
        setPhase('go');
        setCountText('GO!');
      } else if (typeof data.value === 'number') {
        setCount(data.value);
        setCountText(String(data.value));
      }
    });

    socket.on('stake:error', (data) => {
      console.error('[Countdown] Backend error:', data);
      // Non-fatal — the server will continue the countdown flow
    });

    socket.on('stake:gameStarted', (data) => {
      setPhase('started');
      const gId = data.gameId || matchId;
      setTimeout(() => navigateToWhot(gId), 600);
    });

    // ── Cleanup ──
    return () => {
      clearTimeout(countdownTimeout);
      socket.off('connect');
      socket.off('match:countdown');
      socket.off('stake:countdownReady');
      socket.off('match:started');
      socket.off('match:cancelled');
      socket.off('stake:cancelled');
      socket.off('stake:countdown');
      socket.off('stake:gameStarted');
      socket.off('stake:error');
      socket.disconnect();
    };
    // eslint-disable-next-line
  }, [token, matchId]);

  // ============================================================
  // STYLES
  // ============================================================

  const styles = {
    container: {
      minHeight: '100vh',
      background: '#0a0a1a',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      color: '#fff',
    },
    number: {
      fontSize: countText === 'GO!' ? '80px' : '140px',
      fontWeight: 900,
      color: countText === 'GO!' ? '#ff4444' : '#00d4aa',
      textShadow: countText === 'GO!'
        ? '0 0 60px rgba(255,68,68,0.5)'
        : '0 0 60px rgba(0,212,170,0.4)',
      transition: 'all 0.3s ease',
      marginBottom: '20px',
      lineHeight: 1,
    },
    label: {
      fontSize: '20px',
      color: '#888',
      marginBottom: '40px',
    },
    subtitle: {
      fontSize: '14px',
      color: '#555',
      marginBottom: '8px',
    },
    matchInfo: {
      fontSize: '13px',
      color: '#666',
      marginBottom: '32px',
    },
    errorBox: {
      background: 'rgba(255,77,77,0.1)',
      border: '1px solid #ff4444',
      color: '#ff4444',
      padding: '16px 24px',
      borderRadius: '10px',
      textAlign: 'center',
      marginTop: '24px',
      fontSize: '15px',
    },
  };

  // ============================================================
  // RENDER
  // ============================================================

  if (!token) return null;

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes pulseNumber {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
        @keyframes flashGO {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>

      {phase === 'countdown' && (
        <>
          <div style={styles.subtitle}>Game starts in</div>
          <div style={{
            ...styles.number,
            animation: 'pulseNumber 0.8s ease-in-out infinite',
          }}>
            {countText || '...'}
          </div>
          <div style={styles.label}>Get ready!</div>
          <div style={styles.matchInfo}>
            Match: {matchId?.slice(0, 8) || ''} · {gameType?.toUpperCase() || 'GAME'}
          </div>
        </>
      )}

      {phase === 'go' && (
        <>
          <div style={{
            ...styles.number,
            animation: 'flashGO 0.4s ease-in-out infinite',
          }}>
            GO!
          </div>
          <div style={{ ...styles.label, color: '#fff', fontSize: '22px', fontWeight: 700 }}>
            Starting now...
          </div>
        </>
      )}

      {phase === 'started' && (
        <>
          <div style={{
            fontSize: '28px',
            fontWeight: 700,
            color: '#00d4aa',
            marginBottom: '16px',
          }}>
            Game Started!
          </div>
          <div style={styles.label}>
            Loading game board...
          </div>
        </>
      )}

      {phase === 'cancelled' && (
        <>
          <div style={{
            fontSize: '24px',
            fontWeight: 700,
            color: '#ff4444',
            marginBottom: '12px',
          }}>
            Match Cancelled
          </div>
          <div style={styles.errorBox}>
            {errorMsg || 'Match was cancelled.'}
          </div>
          <div style={{ marginTop: '16px', color: '#666', fontSize: '14px' }}>
            You've been refunded. Redirecting...
          </div>
        </>
      )}

    </div>
  );
}
